import { defineComponent as _defineComponent } from 'vue'
import useHttpRequest from "@/composables/use-http-request";
import Examination from "@/interfaces/examination.interface";
import { ref, computed, watch, onUnmounted, Ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import ConfirmStartExaminationDialog from "@/components/dialogs/confirm-start-examination-dialog.vue";
import BrowserNotSupportedWarningDialog from "@/components/dialogs/browser-not-supported-warning-dialog.vue";
import { isBrowserSupported } from "@/helpers/isBrowserSupported";

import { Loading, MessageBox } from "element-ui";


export default /*#__PURE__*/_defineComponent({
  __name: 'start-examination-component',
  props: {
    examination: null
  },
  setup(__props: any) {

const props = __props as {
  examination?: Examination;
}

const examStartInSeconds: Ref<number> = ref(0);
let countDownInterval: number | undefined;
const confirmStartExaminationDialogVisible = ref(false);
const browserNotSupportedWarningDialogVisible = ref(false);

const route = useRoute();
const router = useRouter();



const formattedDateWithTime = (seconds: number) => {
  //Ensure we only start the Count down if it's less than a day
  return seconds >= 86400
    ? "Exam Not Available"
    : `Starts in ${new Date(seconds * 1000).toISOString().substr(11, 8)}`;
};

const hasStudentStartedExam = computed((): boolean => {
  return !!props.examination?.student_examination_submissions?.length;
});

const studentHasSpecialSubmitPermission = computed((): boolean => {
  return (
    hasStudentStartedExam.value &&
    !!props.examination?.student_examination_submissions?.[0]
      ?.is_special_submission_valid
  );
});

const studentSubmitted = computed((): boolean => {
  return (
    hasStudentStartedExam.value &&
    !!props.examination?.student_examination_submissions?.[0]?.submitted_at
  );
});

const canResubmitExam = computed((): boolean => {
  return !!props.examination?.is_available && studentSubmitted.value;
});

const buttonText = computed(() => {
  //first check for special permission
  if (studentHasSpecialSubmitPermission.value) {
    return "Resume Examination";
  }

  if (props.examination?.is_available) {
    if (studentSubmitted.value) {
      return "Already Submitted";
    } else if (hasStudentStartedExam.value) {
      return "Resume Examination";
    } else {
      return "Start Examination";
    }
  } else {
    if (props.examination?.start_in_seconds) {
      return formattedDateWithTime(examStartInSeconds.value);
    }
    //Meaning Now this Exam is in the Past
    else {
      //Find Out, if the  Student had not started the Exam Even. and it's not in the future
      if (!hasStudentStartedExam.value && props.examination?.is_exam_ended) {
        return "You missed this Exam";
      }

      if (studentSubmitted.value) {
        return "Already Submitted";
      }

      return "Exam Not Available";
    }
  }
});

const buttonType = computed((): string => {
  if (buttonText.value === "Already Submitted") {
    return "success";
  } else if (buttonText.value === "You missed this Exam") {
    return "warning";
  }

  return "primary";
});

const buttonIcon = computed(() => {
  if (buttonText.value === "Already Submitted") {
    return "el-icon-circle-check";
  } else if (buttonText.value === "You missed this Exam") {
    return "el-icon-warning-outline";
  }
  return "";
});

const startCountDown = () => {
  countDownInterval = setInterval(() => {
    if (examStartInSeconds.value == 0) {
      clearInterval(countDownInterval);
      return location.reload();
    }
    --examStartInSeconds.value;
  }, 1000);
};

const ensureBrowserIsSupported = (): boolean => {
  if (!isBrowserSupported()) {
    browserNotSupportedWarningDialogVisible.value = true;
    return false;
  }

  return true;
};

const onStartExamination = async () => {
  if (
    !props.examination?.is_available &&
    !studentHasSpecialSubmitPermission.value
  ) {
    return;
  }

  //Ensure the student has a submission and they havent submitted or they have a special submission pass
  if (
    studentHasSpecialSubmitPermission.value ||
    (hasStudentStartedExam.value && !studentSubmitted.value)
  ) {
    //AS A HACK, FORCEFULLY LOAD THE EXAMINATIONS SCREEN AS A NEW HTML PAGE SINCE IN THAT WAY, TINY MCE LOADS WELL
    return (window.location.href = `/dashboard/examinations/ongoing/${props.examination?.examination_id}`);
  }

  let loadingIndicator = Loading.service({
    lock: true,
    text: "Starting Examination...",
    background: "rgba(0, 0, 0, 0.7)",
  });

  const request = await useHttpRequest<{ success: boolean; message: string }>({
    method: "POST",
    url: "examinations/start-examination/",
    loadingProperty: null,
    showSuccessMessage: false,
    showFullScreenLoader: false,
    errorLoadingProperty: null,
    body: {
      examination_id: props.examination?.examination_id,
      course_unit_id: route.params.courseUnitId,
    },
  });

  if (!request) {
    loadingIndicator.close();
    return;
  }

  if (
    request.success &&
    request.message === "Examination started successfully"
  ) {
    //AS A HACK, FORCEFULLY LOAD THE EXAMINATIONS SCREEN AS A NEW HTML PAGE SINCE IN THAT WAY, TINY MCE LOADS WELL
    window.location.href = `/dashboard/examinations/ongoing/${props.examination?.examination_id}`;
    /* this.$router.push({
      path: `/dashboard/examinations/ongoing/${this.examination.examination_id}`,
    }); */
  } else {
    loadingIndicator.close();
    router.replace({ path: "/dashboard/examinations" });
  }
};

const onStartExamIntent = async () => {
  try {
    // Ensure button text is "Start Examination", if it is show the disclaimer as it means this the first time the student is starting the exam
    // it's okay to use the button text from the DOM which would be manipulated, but since this is not a critical issue i.e just a
    // disclaimer message, we can use it.
    if (buttonText.value === "Start Examination") {
      //But only proceed if the browser is supported
      if (ensureBrowserIsSupported()) {
        confirmStartExaminationDialogVisible.value = true;
      } else {
        return;
      }
      // confirmStartExaminationDialogVisible.value = true;
    } else {
      onStartExamination();
    }
  } catch (error) {
    // eslint-disable-line no-unused-vars
  }
};

const fetchedExam = computed((): Examination | null => {
  return props.examination ?? null;
});

const onContinueToExam = () => {
  confirmStartExaminationDialogVisible.value = false;
  onStartExamination();
};

const onResubmitExamIntent = async () => {
  const confirm = await MessageBox.alert(
    "You've already submitted this examination. Are you sure you want to submit it again? Resubmissions are only allowed while the exam is still ongoing",
    {
      title: "Resubmit Answer Sheet?",
      confirmButtonText: "Yes, Submit Again",
      cancelButtonText: "Cancel",
      showCancelButton: true,
      distinguishCancelAndClose: true,
      closeOnClickModal: true,
      closeOnPressEscape: true,
    }
  );

  if (confirm === "confirm") {
    let loadingIndicator = Loading.service({
      lock: true,
      text: "Please Wait...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      examination: Examination;
    }>({
      method: "GET",
      url: `examinations/ongoing-examination/${
        props.examination?.examination_id
      }?timestamp=${new Date().getTime()}`,
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
      showFullScreenLoader: false,
    });

    if (!request) {
      loadingIndicator.close();
      return;
    }

    if (
      request.success &&
      request.message == "ONGOING EXAMINATION FETCHED SUCCESSFULLY"
    ) {
      window.location.href = `/dashboard/examinations/ongoing/${props.examination?.examination_id}`;
    } else {
      return loadingIndicator.close();
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
watch(fetchedExam, () => {
  if (props.examination?.start_in_seconds) {
    examStartInSeconds.value = props.examination.start_in_seconds;
    startCountDown();
  }
});

onUnmounted(() => {
  if (countDownInterval) {
    clearInterval(countDownInterval);
  }
});

return { __sfc: true,examStartInSeconds, countDownInterval, confirmStartExaminationDialogVisible, browserNotSupportedWarningDialogVisible, route, router, props, formattedDateWithTime, hasStudentStartedExam, studentHasSpecialSubmitPermission, studentSubmitted, canResubmitExam, buttonText, buttonType, buttonIcon, startCountDown, ensureBrowserIsSupported, onStartExamination, onStartExamIntent, fetchedExam, onContinueToExam, onResubmitExamIntent, ConfirmStartExaminationDialog, BrowserNotSupportedWarningDialog }
}

})